import { ref } from 'vue'
import axios from 'axios'
import store from '@/store/index'

export function useAnalytics() {
  const loading = ref(false)
  const error = ref(null)
  const summary = ref(null)
  const suppliers = ref([])
  const supplierDetail = ref(null)
  const trends = ref(null)
  const zones = ref([])
  const groupAnalytics = ref(null)
  // Dashboard decision widgets. These do not touch the shared `loading`/`error`: a widget
  // that fails shows its own inline message instead of blanking the page. Each value is
  // the API payload, or { error } when the request failed.
  const health = ref(null)
  const outcomes = ref(null)
  const movers = ref(null)
  const billing = ref(null)

  const apiUrl = store.state.apiUrl

  const fetchSummary = async (siteId, startDate, endDate) => {
    loading.value = true
    error.value = null
    try {
      const res = await axios.get(`${apiUrl}/analytics/summary`, {
        params: { site_id: siteId, start_date: startDate, end_date: endDate }
      })
      summary.value = res.data
    } catch (e) {
      error.value = e.response?.data?.detail || e.message
      console.error('[Analytics] Summary error:', e)
    } finally {
      loading.value = false
    }
  }

  const fetchSuppliers = async (siteId, startDate, endDate) => {
    loading.value = true
    error.value = null
    try {
      const res = await axios.get(`${apiUrl}/analytics/suppliers`, {
        params: { site_id: siteId, start_date: startDate, end_date: endDate }
      })
      suppliers.value = res.data
    } catch (e) {
      error.value = e.response?.data?.detail || e.message
      console.error('[Analytics] Suppliers error:', e)
    } finally {
      loading.value = false
    }
  }

  const fetchSupplierDetail = async (supplierId, startDate, endDate) => {
    loading.value = true
    error.value = null
    try {
      const res = await axios.get(`${apiUrl}/analytics/supplier/${supplierId}`, {
        params: { start_date: startDate, end_date: endDate }
      })
      supplierDetail.value = res.data
    } catch (e) {
      error.value = e.response?.data?.detail || e.message
      console.error('[Analytics] Supplier detail error:', e)
    } finally {
      loading.value = false
    }
  }

  const fetchTrends = async (siteId, startDate, endDate) => {
    loading.value = true
    error.value = null
    try {
      const res = await axios.get(`${apiUrl}/analytics/trends`, {
        params: { site_id: siteId, start_date: startDate, end_date: endDate }
      })
      trends.value = res.data
    } catch (e) {
      error.value = e.response?.data?.detail || e.message
      console.error('[Analytics] Trends error:', e)
    } finally {
      loading.value = false
    }
  }

  const fetchZones = async (siteId, startDate, endDate) => {
    loading.value = true
    error.value = null
    try {
      const res = await axios.get(`${apiUrl}/analytics/zones`, {
        params: { site_id: siteId, start_date: startDate, end_date: endDate }
      })
      zones.value = res.data
    } catch (e) {
      error.value = e.response?.data?.detail || e.message
      console.error('[Analytics] Zones error:', e)
    } finally {
      loading.value = false
    }
  }

  const fetchGroupAnalytics = async (groupId, startDate, endDate) => {
    loading.value = true
    error.value = null
    try {
      const res = await axios.get(`${apiUrl}/analytics/groups/${groupId}`, {
        params: { start_date: startDate, end_date: endDate }
      })
      groupAnalytics.value = res.data
    } catch (e) {
      error.value = e.response?.data?.detail || e.message
      console.error('[Analytics] Group error:', e)
    } finally {
      loading.value = false
    }
  }

  const downloadReport = async (siteId, startDate, endDate, scope, scopeId, format) => {
    try {
      const res = await axios.post(`${apiUrl}/reports/generate`, {
        site_id: siteId, start_date: startDate, end_date: endDate,
        scope, scope_id: scopeId, format,
      }, { responseType: 'blob' })

      const ext = format === 'csv' ? 'xlsx' : 'pdf'
      const url = window.URL.createObjectURL(new Blob([res.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `analytics_report.${ext}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) {
      error.value = e.response?.data?.detail || e.message
      console.error('[Analytics] Download error:', e)
    }
  }

  const emailReport = async (payload) => {
    try {
      const res = await axios.post(`${apiUrl}/reports/email`, payload)
      return res.data
    } catch (e) {
      error.value = e.response?.data?.detail || e.message
      console.error('[Analytics] Email error:', e)
      throw e
    }
  }

  const fetchReportHistory = async (siteId) => {
    try {
      const res = await axios.get(`${apiUrl}/reports/history`, {
        params: { site_id: siteId }
      })
      return res.data
    } catch (e) {
      error.value = e.response?.data?.detail || e.message
      console.error('[Analytics] History error:', e)
      return []
    }
  }

  const fetchSchedules = async (siteId) => {
    try {
      const res = await axios.get(`${apiUrl}/reports/schedules`, {
        params: { site_id: siteId }
      })
      return res.data
    } catch (e) {
      error.value = e.response?.data?.detail || e.message
      return []
    }
  }

  const createSchedule = async (payload) => {
    const res = await axios.post(`${apiUrl}/reports/schedules`, payload)
    return res.data
  }

  const updateSchedule = async (id, payload) => {
    const res = await axios.put(`${apiUrl}/reports/schedules/${id}`, payload)
    return res.data
  }

  const deleteSchedule = async (id) => {
    const res = await axios.delete(`${apiUrl}/reports/schedules/${id}`)
    return res.data
  }

  const runScheduleNow = async (id) => {
    const res = await axios.post(`${apiUrl}/reports/schedules/${id}/run`)
    return res.data
  }

  const quietGet = async (path, params, target) => {
    try {
      const res = await axios.get(`${apiUrl}${path}`, { params })
      if (target) target.value = res.data
      return res.data
    } catch (e) {
      console.error(`[Analytics] ${path} error:`, e)
      const payload = { error: e.response?.data?.detail || e.message }
      if (target) target.value = payload
      return payload
    }
  }
  const fetchHealth = (siteId) => quietGet('/analytics/health', { site_id: siteId }, health)
  const fetchOutcomes = (siteId, startDate, endDate) =>
    quietGet('/analytics/outcomes', { site_id: siteId, start_date: startDate, end_date: endDate }, outcomes)
  const fetchMovers = (siteId, startDate, endDate) =>
    quietGet('/analytics/movers', { site_id: siteId, start_date: startDate, end_date: endDate }, movers)
  const fetchBillingExceptions = (siteId, startDate, endDate) =>
    quietGet('/analytics/billing-exceptions', { site_id: siteId, start_date: startDate, end_date: endDate }, billing)
  // A summary for another range WITHOUT replacing `summary` (used for "vs last month").
  const fetchSummaryFor = (siteId, startDate, endDate) =>
    quietGet('/analytics/summary', { site_id: siteId, start_date: startDate, end_date: endDate }, null)

  return {
    loading, error,
    summary, suppliers, supplierDetail, trends, zones, groupAnalytics,
    health, outcomes, movers, billing,
    fetchHealth, fetchOutcomes, fetchMovers, fetchBillingExceptions, fetchSummaryFor,
    fetchSummary, fetchSuppliers, fetchSupplierDetail, fetchTrends, fetchZones,
    fetchGroupAnalytics,
    downloadReport, emailReport, fetchReportHistory,
    fetchSchedules, createSchedule, updateSchedule, deleteSchedule, runScheduleNow,
  }
}
